import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  activeSessionExpiryNotice,
  dismissSessionExpiry,
  sessionExpiryLoginTarget,
  subscribeSessionExpiry,
  type SessionExpiryNotice
} from "@/lib/client/session-expiry";

/**
 * 会话超时或被顶掉时的全局弹窗。
 *
 * 在这之前，这类 401 只会变成页面里一句"请重新登录"加一个"重新加载"按钮：
 * 重新加载必然再失败一次，而页面上根本没有回登录页的入口，用户只能自己
 * 去右上角点"退出"。所以这里改成弹窗接管——提示是弹窗，出口也在弹窗上，
 * 按钮直接把人送回登录页。
 */
export function SessionExpiredDialog() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const location = useLocation();
  const [notice, setNotice] = useState<SessionExpiryNotice | null>(() => activeSessionExpiryNotice());
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => subscribeSessionExpiry((next) => {
    setNotice(next.status === "active" ? next.notice : null);
  }), []);

  if (!notice) return null;

  const target = sessionExpiryLoginTarget(notice, location.pathname);
  const copyKey = notice.reason === "replaced" ? "replaced" : "expired";

  function goToLogin() {
    dismissSessionExpiry();
    // 动态 import: spa-api 是后台页面才用的大模块，静态引进来会把它拖进首屏 chunk。
    // 弹窗弹出来时它必然已经加载过了，这里只是把失效的会话缓存清掉。
    void import("../spa-api").then((module) => module.invalidateSessionCache()).catch(() => {});
    navigate(target.path, { replace: true, state: { redirectTo: target.redirectTo, reason: t(`sessionExpired.${copyKey}.title`) } });
  }

  return (
    <div className="spa-modal-backdrop spa-session-expired-backdrop" role="presentation">
      <section
        className="spa-modal-card spa-session-expired-card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <span className="tag">{t("sessionExpired.tag")}</span>
        <h2 id={titleId}>{t(`sessionExpired.${copyKey}.title`)}</h2>
        <p id={descriptionId}>{t(`sessionExpired.${copyKey}.description`)}</p>
        <div className="spa-session-expired-actions">
          {/* 弹窗只有这一个出口，焦点直接落上去，键盘用户不用自己找 */}
          <button className="spa-btn primary" type="button" onClick={goToLogin} autoFocus>
            {t("sessionExpired.action")}
          </button>
        </div>
      </section>
    </div>
  );
}
